import { Component, Inject, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';
import * as Color from 'color';
import { Issue, IssueOverviewModel, PaginationPage, PaginationResponse, WidgetFilter, WidgetType } from 'src/app/classes';
import { AbstractListWidgetInner, WidgetInnerView } from 'src/app/widget-inner';
import { FilterTypes } from '../../../edit/edit-userfilter/edit-userfilter.component';
import { executeIssueTransaction, SpecialIssueTransactionRole } from 'src/app/lib/issue/globals';
import { IssueTransactionService } from 'src/app/lib/issue/services/issue-transaction.service';
import { ISSUE_PROVIDER_SERVICE, ISSUE_TRANSACTION_PROVIDER_SERVICE } from 'src/app/lib/issue/interfaces';
import { IssueWidgetInnerComponentService } from './issue-widget-inner-service';
import { IssueService } from 'src/app/lib/issue/services/issue.service';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { FilterService } from 'src/app/services/filter.service';
import { IWidgetProviderService, WIDGET_PROVIDER_SERVICE } from 'src/app/interfaces';
import { WidgetTypeMap } from '../../widget-globals';
import { showAnimation } from 'src/animations';
import { IssueTypes } from 'src/app/lib/shared/globals';
import { widgetAdderColor } from 'src/app/globals';
import { IssueTransaction } from 'src/app/lib/issue/classes';

@Component({
  selector: 'app-issue-widget-inner',
  templateUrl: './issue-widget-inner.component.html',
  styleUrls: ['./issue-widget-inner.component.css'],
  animations: [showAnimation],
  providers: [
    { provide: ISSUE_PROVIDER_SERVICE, useClass: IssueWidgetInnerComponentService },
    { provide: ISSUE_TRANSACTION_PROVIDER_SERVICE, useExisting: ISSUE_PROVIDER_SERVICE }
  ]
})
export class IssueWidgetInnerComponent extends AbstractListWidgetInner<IssueOverviewModel> {

  issueTypeId: number;
  title: string = null;
  currentView;

  viewTitle: string = null;

  isIssueType: boolean = false;
  isIncidentType: boolean = false;
  isChangeType: boolean = false;
  isProjectType: boolean = false;

  constructor(
    @Inject(ISSUE_PROVIDER_SERVICE) private _issueWidgetInnerService: IssueWidgetInnerComponentService,
    @Inject(WIDGET_PROVIDER_SERVICE) private _widgetComponentService: IWidgetProviderService,
    private _issueService: IssueService,
    private _filterService: FilterService,
    private _router: Router,
    private _issueTransactionService: IssueTransactionService,
    private _viewContainerRef: ViewContainerRef
  ) {
    super();
    this.sortAttribute = "issuenumber";

    this.stringSearchProvider = (...args) => this._issueService[this.isChangeType ? 'searchChangesByString' : this.isIncidentType ? 'searchIncidentsByString' : this.isIssueType ? 'searchIssuesByString' : 'searchProjectsByString'](...args);
    this.userFilterSearchProvider = (...args) => this._issueService.searchIssuesByUserFilter(...args);
  }

  addIssue() {
    var type = null;

    if (this.isChangeType) {
      type = IssueTypes.Change;
    } else if (this.isIncidentType) {
      type = IssueTypes.Incident;
    } else if (this.isIssueType) {
      type = IssueTypes.Issue;
    }

    (this._issueTransactionService.getIssueTransactionBySpecialRole(this.isChangeType? SpecialIssueTransactionRole.CREATE_CHANGE: this.isIncidentType? SpecialIssueTransactionRole.CREATE_INCIDENT: SpecialIssueTransactionRole.CREATE_ISSUE).pipe(take(1), switchMap((transaction) => executeIssueTransaction(this._issueTransactionService, transaction.guid, this._viewContainerRef, () => {
      this._issueWidgetInnerService.provideEmptyIssue();
      this._issueWidgetInnerService.setIssueType(type);
    })))).toPromise();

  }

  getBackgroundColor(issue: IssueOverviewModel): string {
    if (issue.statecolor) {
      var color = Color(issue.statecolor).alpha(.3).rgb().toString();
      return color;
    }
    return null;
  }

  init(widgetType: WidgetType) {

    this.isChangeType = widgetType.wtypename === WidgetTypeMap.ChangeWidget.typeName;
    this.isIncidentType = widgetType.wtypename === WidgetTypeMap.IncidentWidget.typeName;
    this.isIssueType = widgetType.wtypename === WidgetTypeMap.IssueWidget.typeName;
    this.isProjectType = widgetType.wtypename === WidgetTypeMap.ProjectWidget.typeName;

    var name;

    if (this.isChangeType) {
      this.currentFilterType = FilterTypes.find(x => x.value == "Change");
      this.title = "RFCs";
      name = "Neuen Change anlegen";
    }
    
    if (this.isIncidentType) {
      this.currentFilterType = FilterTypes.find(x => x.value == "Incident");
      this.title = "Tickets";
      name = "Neues Ticket anlegen";
    }

    if (this.isIssueType) {
      this.currentFilterType = FilterTypes.find(x => x.value == "Issue");
      this.title = "Vorgänge";
      name = "Neuen Vorgang anlegen";
    }

    if (this.isProjectType) {
      this.currentFilterType = FilterTypes.find(x => x.value == "Project");
      this.title = "Projekte";
      name = "Neues Projekt anlegen";
    }

    this._widgetComponentService.setWidgetInnerViews([
      {
        name: () => of(name),
        icon: () => of('add'),
        onClick: () => {
          this.addIssue();
          return of(null);
        },
        color: () => of(widgetAdderColor)
      } as WidgetInnerView
    ]);

    this.setInitialDefaultView();
  }

  issueClicked = (issue: Issue) => {
    this._router.navigate(['/issue-details', issue.issueid]);
  }

  ngOnInit(): void {
    this.viewSubscriptions.push(...[
      this._widgetComponentService.widgetType$.subscribe(x => this.init(x))
    ]);
  }

  setDefaultView(view) {
    let url = this.isProjectType ? 'project' : this.isIncidentType ? 'incident' : this.isChangeType ? 'change' : 'issue';

    this.currentView = view;
    if (view.title) this.viewTitle = view.title;
    if (view.sortAttribute) this.sortAttribute = view.sortAttribute;
    if (view.sortDsc) this.sortDsc = view.sortDsc;

    this.userFilterIdentifier = 0;

    this.updateContentProvider(this.page.asObservable().pipe(switchMap((page: PaginationPage) => {
      return view.provider(this._issueService, url, page) as Observable<PaginationResponse<IssueOverviewModel[]>>;
    })));
  }

  setInitialDefaultView() {
    this.showSearchInput = false;
    this.setDefaultView(IssueWidgetDefaultViews[0]);
  }

  setSorting(criteria: { value: string, title: string }) {
    this._filterService
      .updateUserFilterSorting(this.userFilterIdentifier, criteria.value, criteria.value === this.sortAttribute ? !this.sortDsc : false)
      .pipe(switchMap((filter: WidgetFilter) => combineLatest([of(filter), this._widgetComponentService.widget$.pipe(map(x => x.widgetuserfilters.find(y => y.filterid === filter.filterid)))])))
      .subscribe(([updatedFilter, oldFilter]) => {
        oldFilter.filtersortattribute = updatedFilter.filtersortattribute;
        oldFilter.filtersortdsc = updatedFilter.filtersortdsc;
        this.receiveUserFilter(oldFilter);
      });
  }

  IssueWidgetDefaultViews = IssueWidgetDefaultViews;
}

export const IssueWidgetDefaultViews = [
  {
    title: "Zuletzt verwendet",
    icon: "history",
    sortAttribute: "accessed",
    sortDsc: true,
    provider: function (issueService: IssueService, uri: string, page: PaginationPage): Observable<PaginationResponse<IssueOverviewModel[]>> {
      return issueService.getUserIssueHistory(uri, page);
    }
  }, {
    title: "Zuletzt erstellt",
    icon: "fiber_new",
    provider: function (issueService: IssueService, uri: string, page: PaginationPage): Observable<PaginationResponse<IssueOverviewModel[]>> {
      return issueService.getLatestIssues(uri, page);
    }
  }, {
    title: "Beobachtete",
    icon: "visibility",
    provider: function (issueService: IssueService, uri: string, page: PaginationPage): Observable<PaginationResponse<IssueOverviewModel[]>> {
      return issueService.getWatchedIssues(uri, page);
    }
  }, {
    title: "Eingeteilt",
    icon: "work",
    provider: function (issueService: IssueService, uri: string, page: PaginationPage): Observable<PaginationResponse<IssueOverviewModel[]>> {
      return issueService.getAssignedIssues(uri, page);
    }
  }, {
    title: "Ungesehen",
    icon: "fiber_new",
    provider: function (issueService: IssueService, uri: string, page: PaginationPage): Observable<PaginationResponse<IssueOverviewModel[]>> {
      return issueService.getUnseenIssues(uri, page);
    }
  }
];
